import React, { useCallback } from 'react';
import * as Styled from './styles';
import PhoneIcon from 'assets/svg/phone-1-5.svg';
import { useLocale } from 'locale';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { ContactFormSubmitData, FormFieldMode, FormFields } from '../common/Fields';
import { FormTitle } from '../common/styled';
import { connect } from 'react-redux';
import { setIsAgentOfficeContactPopupOpen, submitUserContactAgent } from 'store/state/app/actions';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { IContactInfo } from 'screens/AgentOfficePages/types';
import { RootAction, State } from 'store/state';
import { userProfileSelector } from 'store/state/domainData/selectors';
import { IAgentPageData, IOfficePageData, User } from 'utils/entities';
import { NoImg } from 'screens/AgentOfficePages/common/components/NoImg';
import { ContactAgentSubmitterSource, SubmitContactAgentPayload } from 'store/state/app/payloads';
import { Dispatch } from 'redux';
import { AdditionalDataPageType, IContactPageSubmitVariables, LeadType } from 'store/sagas/apiService/types';
import { contactAgentPageResponseSelector } from 'store/state/mutationsResponse/selectors';
import withEvents from 'analytics/withEvents';
import { agentPageDataSelector } from 'store/state/domainData/selectors/agentById';
import { officePageDataSelector } from 'store/state/domainData/selectors/officeById';
import { noop } from 'lodash';
import { SpacingBlock } from 'components/spacing-block';
import { LeadContactTermsAndConditions } from 'components/term-and-conditions/LeadContactTermsAndConditions';

interface StaticContactProps extends IContactInfo {
  user: User;
  formOnly?: boolean;
  noShadow?: boolean;
  isLeadingAgent?: boolean;
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => void;
  id: string;
  isContacted: boolean;
  onPhoneFieldClick?: (props: StaticContactProps, isAgent: boolean) => void;
  agentPageInfo?: IAgentPageData;
  officePageInfo?: IOfficePageData;
  onPhoneExpose?: (source: string, props: StaticContactProps) => void;
}

export const StaticContactFormBase: React.FC<StaticContactProps> = (props) => {
  const {
    id,
    user,
    logo,
    title,
    phone,
    name,
    isAgent,
    formOnly,
    noShadow = false,
    onContactPageSubmit,
    isContacted,
    onPhoneFieldClick,
    onPhoneExpose = noop,
  } = props;
  const { t } = useLocale();
  const isMobile = useScreenBreakpoint() === 1;
  const isTermsAndConditionsVisible = !isContacted;

  const constructImageURI = useImageUrlBuilder();
  const imageURL = constructImageURI(logo);

  const handlePhoneClickEvent = () => {
    onPhoneFieldClick(props, isAgent);
  };

  const handleContactSubmit = useCallback((data: ContactFormSubmitData) => {
    onContactPageSubmit({
      key: id,
      variables: {
        id,
        email: data.userEmail,
        name: data.userName,
        phone: data.userPhone,
        type: isAgent ? LeadType.Agent : LeadType.Office,
        additionalData: {
          pageType: isAgent ? AdditionalDataPageType.NewAgentPage : AdditionalDataPageType.NewOfficePage,
        } as IContactPageSubmitVariables['additionalData'],
      },
      source: ContactAgentSubmitterSource.Form,
    });
  }, []);

  const handleStatisPhoneClickEvent = () => {
    onPhoneExpose(isAgent ? 'agent_page' : 'office_page', props);
  };

  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  const userPhone = user ? user.phone : '';
  const userEmail = user ? user.email : '';

  return (
    <Styled.Wrapper noShadow={noShadow}>
      {isMobile || formOnly ? null : (
        <Styled.SectionLogo>
          {!isAgent ? <Styled.Logo>{imageURL ? <img src={imageURL} alt={name} /> : <NoImg size={48} />}</Styled.Logo> : null}
          {isAgent ? <Styled.AgentLogo>{imageURL ? <img src={imageURL} alt={name} /> : <NoImg size={92} />}</Styled.AgentLogo> : null}
          {name ? <Styled.Title weight="bold">{name}</Styled.Title> : null}
          {!isAgent && phone ? <Styled.Phone onClick={handleStatisPhoneClickEvent} href={createPhoneURI(phone)} weight="medium">{phone}<PhoneIcon /></Styled.Phone> : null}
          {isAgent && title ? <Styled.AgentTitle>{title}</Styled.AgentTitle> : null}
        </Styled.SectionLogo>
      )}
      <Styled.FormWrapper>
        {isContacted && !formOnly && !isMobile ? null : <FormTitle weight="bold">{t('madad.banner.winners.submit')}</FormTitle>}
        <FormFields
          onSubmit={handleContactSubmit}
          successfullySent={isContacted}
          withSubmitButton
          mode={isMobile || formOnly ? FormFieldMode.Column : FormFieldMode.Row}
          userData={{ userPhone, userName, userEmail }}
          handlePhoneClickEvent={handlePhoneClickEvent}
        />
        {(isMobile || formOnly) && phone ? (
          <Styled.OrByPhone>
            <Styled.OrByPhoneText>{t('officePage.contact.text.orByPhone')}</Styled.OrByPhoneText>
            <Styled.MobilePhone onClick={handleStatisPhoneClickEvent} weight="bold" href={createPhoneURI(phone)}>{phone}</Styled.MobilePhone>
          </Styled.OrByPhone>
        ) : null}
        {isTermsAndConditionsVisible ? (
          <SpacingBlock mTop={2}>
            <LeadContactTermsAndConditions />
          </SpacingBlock>
        ) : null}
      </Styled.FormWrapper>
    </Styled.Wrapper>
  );
};

const StaticContactFormBaseWithEvents = withEvents<StaticContactProps>((sendEvent) => ({
  onPhoneFieldClick: (props: StaticContactProps, isAgent: boolean) => {
    sendEvent('user_contact_me_form_phone_text_box_click', 'user', {
      event: {
        agent_details: isAgent ? {
          id: props.id,
          name: props.agentPageInfo.name,
          office_id: props.agentPageInfo.officeId,
          office_name: props.officePageInfo.officeName,
          phone_number: props.phone,
        } : {
          id: props.officePageInfo.officeId,
          office_id: props.officePageInfo.officeId,
          office_name: props.officePageInfo.officeName,
          phone_number: props.phone,
        },
        source: isAgent ? 'agent_page' : 'office_page',
      },
    });
  },
  onPhoneExpose: (source: string, props: StaticContactProps) => {
    sendEvent('user_contact_me_call_submit', 'user', {
      ...('agent_page' === source ? {
        property: {
          property_id: 'property_id',
          deal_type: 'for_sale',
          bulletin_info: {
            agent_details: {
              id: props.id,
              name: props.agentPageInfo.name,
              office_id: props.agentPageInfo.officeId,
              office_name: props.officePageInfo.officeName,
              phone_number: props.phone,
              is_leading_agent: props.isLeadingAgent,
            },
          },
        },
      } : {}),
      event: {
        source,
      },
      rtbData: {
        phone: props.phone,
      },
    });
  },
}))(StaticContactFormBase);

const mapStateToProps = (state: State, props: { id: string }) => {
  const contactResponse = contactAgentPageResponseSelector(state, props.id);
  const isContacted = Boolean(contactResponse && contactResponse.data && contactResponse.data.contactV4);
  return {
    isContacted,
    user: userProfileSelector(state),
    agentPageInfo: agentPageDataSelector(state),
    officePageInfo: officePageDataSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => dispatch(submitUserContactAgent(payload)),
  setContactOpen: setIsAgentOfficeContactPopupOpen,
});

export const StaticContactForm = connect(mapStateToProps, mapDispatchToProps)(StaticContactFormBaseWithEvents);
